import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { restartableTask, timeout } from 'ember-concurrency';
import { typeOf } from '@ember/utils';
import { paesiMondoAlfa3, paesiMondoAlfa2 } from 'beebee-base/lib/list_taxes';
import { inject as iService } from '@ember/service';

export default class CheckinDetailComponent extends Component {
  @iService() store;
  @iService() notify;
  @iService() session;

  // Disabled-state flags computed on insert/item-change
  @tracked firstNameDisabled = false;
  @tracked lastNameDisabled = false;
  @tracked ragioneSocialeDisabled = false;
  @tracked fisicaGiuridicaDisabled = false;

  // --- lifecycle ---

  @action
  setup(element) {
    this.disableField();
    this.checkAddressTask.perform();
  }

  @action
  onItemChange(element, [item]) {
    if (item?.isDeleted || item?.isDeleting || item?.isDestroying) return;
    this.disableField();
    this.checkAddressTask.perform();
    if (item?.id) item.reload();
  }

  // --- helpers ---

  disableField() {
    this.firstNameDisabled =
      this.args.item?.get('first_name') && this.args.item.get('first_name') !== '';

    this.lastNameDisabled =
      this.args.item?.get('last_name') && this.args.item.get('last_name') !== '';

    this.ragioneSocialeDisabled =
      this.args.item?.get('fisica_giuridica') &&
      this.args.item?.get('ragione_sociale') &&
      this.args.item.get('ragione_sociale') !== '';

    let fisicaGiuridicaDisabled = false;
    if (this.args.item?.get('fisica_giuridica')) {
      fisicaGiuridicaDisabled =
        this.args.item?.get('ragione_sociale') && this.args.item.get('ragione_sociale') !== '';
    } else {
      fisicaGiuridicaDisabled =
        this.args.item?.get('first_name') &&
        this.args.item.get('first_name') !== '' &&
        this.args.item?.get('last_name') &&
        this.args.item.get('last_name') !== '';
    }
    this.fisicaGiuridicaDisabled = fisicaGiuridicaDisabled;
  }

  // --- getters ---

  get hide_field() {
    return this.session.option_field?.booking_settings?.checkin_hide_field || {};
  }

  get not_required_field() {
    return this.session.option_field?.booking_settings?.checkin_not_required_field || {};
  }

  get isDateRequired() {
    return !this.not_required_field?.data_nascita;
  }

  get isBirthRequired() {
    return !this.not_required_field?.luogo_nascita;
  }

  get isMobileRequired() {
    return !this.not_required_field?.mobile;
  }

  get isAddressRequired() {
    return !this.not_required_field?.address;
  }

  get showCodFiscale() {
    const firstAddress = this.args.item?.get('addresses')?.[0];
    if (!firstAddress) return false;

    const country = firstAddress.get('location_address_country');
    const countryCode = firstAddress.get('location_address_country_code');

    return country === 'Italia' || ['IT', 'ITA'].includes(countryCode);
  }

  get isCFRequired() {
    const addresses = this.args.item?.get('addresses');
    const country = addresses?.[0]?.get('location_address_country');
    if (country !== 'Italia') return false;
    return !this.not_required_field?.cod_fiscale;
  }

  // --- tasks ---

  @restartableTask
  *checkAddressTask() {
    yield timeout(150);

    if (
      !this.args.item.get('addresses') ||
      this.args.item.get('addresses.length') === 0
    ) {
      let newAddress = this.store.createRecord('address', {
        is_invoice: true,
        location_address_country: 'Italia',
        location_address_country_code: 'IT',
      });
      this.args.item.get('addresses').push(newAddress);
    }
  }

  // --- actions ---

  @action
  formSubmit(e) {
    // Prevent native form submission (action='#' GET) on Enter, which would
    // navigate/reset the SPA and drop unsaved state. Mirrors checkout-detail.
    e?.preventDefault();
    if (typeOf(this.args.confirm) === 'function') {
      this.args.confirm();
    }
  }
}
